import React, { FC, ReactNode, isValidElement, ChangeEvent, useState, useEffect } from 'react';
import classNames from 'classnames/bind';

import Text, { TextProps } from '../Text';

import styles from './index.pcss';

export type Id = string | number;
export type Size = 'xs' | 'sm' | 'md';
export type LabelType = string | ReactNode;

interface RadioProps {
  id: Id;
  checked?: boolean;
  disabled?: boolean;
  onChange: (id: Id) => void;
  /** label for checkbox, could be string or ReactNode */
  label?: LabelType;
  inputAriaLabel?:string;
  /** Text component Props for label */
  labelTextProps?: Partial<TextProps>;
  /**
   * Radio size:
   * `xs`: 19px
   * `sm`: 24px
   * `md`: 28px
   */
  size?: Size;
  /**
   * margin between Radio and label:
   * `sm`: 8px
   * `md`: 16px
   */
  gutter?: Size;
  htmlName?: string;
  wrapperClassName?: string;
  isAjioWalletDisbled?: boolean;
}

const renderLabel: (
  label: LabelType,
  labelTextProps: Partial<TextProps> | undefined,
  labelCls: string,
  key: string
) => ReactNode | null = (element, labelTextProps, labelCls, key) => {
  let labelNode = null;
  if (isValidElement(element)) {
    labelNode = (
      <div aria-hidden="true">
        <label className={labelCls} htmlFor={key} >
        {element}
      </label>
      </div>
    );
  } else if (element) {
    const textProps: TextProps = {
      variant: 'T3',
      color: 'Nero',
      children: [element],
      ariaHidden:true,
      ...labelTextProps
    };
    labelNode = (
      <div aria-hidden="true">
        <label className={labelCls} htmlFor={key}>
        {Text(textProps)}
      </label>
      </div>
    );
  }
  return labelNode;
};

const Radio: FC<RadioProps> = (props: RadioProps) => {
  const {
    checked,
    disabled,
    onChange,
    label,
    labelTextProps,
    size = 'sm',
    gutter = 'sm',
    id,
    htmlName,
    wrapperClassName,
    isAjioWalletDisbled,
    inputAriaLabel=''
  } = props;

  const [active, setActive] = useState<boolean>(() => !!checked);

  useEffect(() => {
    setActive(!!checked);
  }, [checked]);

  const handleChange: (event: ChangeEvent<HTMLInputElement>) => void = event => {
    if (isAjioWalletDisbled) return;

    const isActive = event.target.checked;
    setActive(isActive);
    if (onChange) onChange(id);
  };

  const cx = classNames.bind(styles);
  const containerCls = cx(
    'container',
    {
      [`container-${size}`]: !!size,
      'is-disabled': !!disabled
    },
    [wrapperClassName]
  );

  const inputCls = cx('radio', {
    [`radio-${size}`]: !!size
  });
  const checkContainerCls = cx('check-container', {
    [`check-container-${size}`]: !!size
  });
  const checkCls = size === 'xs' ? cx('check-xs') : cx('check');
  const labelCls = cx('label', {
    [`label-${gutter}`]: !!gutter
  });

  const key = id !== undefined ? id.toString() : '';

  return (
    <div className={containerCls} key={key}>
      <input
        id={key}
        name={htmlName}
        type="radio"
        checked={active}
        onChange={handleChange}
        disabled={disabled}
        className={inputCls}
        aria-label={inputAriaLabel || label}
      />
      <div className={checkContainerCls}>{active ? <div className={checkCls} /> : null}</div>
      {renderLabel(label, labelTextProps, labelCls, key)}
    </div>
  );
};

Radio.defaultProps = {
  disabled: false,
  checked: false,
  labelTextProps: {},
  size: 'sm',
  gutter: 'sm',
  htmlName: '',
  label: '',
  wrapperClassName: ''
};

export default Radio;
